import { Transition } from "@headlessui/react";
import { AlertCircle, CheckCircle } from "@untitled-ui/icons-react";
import classNames from "classnames";
import React, { Fragment, useEffect } from "react";

import type { NotificationType } from "@/utils";

import Button from "../../forms/Button";
import Alert from "../../helpers/Alert";
import FeatureIcon from "../../helpers/FeatureIcon";

const textColors = {
  primary: "text-primary-700",
  warning: "text-warning-700",
  success: "text-success-700",
  error: "text-error-700",
  gray: "text-gray-700",
};

const typeIcons = {
  primary: CheckCircle,
  success: CheckCircle,
  warning: AlertCircle,
  error: AlertCircle,
};

export default function NotificationItem({
  notification,
  onRequestHide,
}: {
  notification: NotificationType;
  onRequestHide: () => void;
}) {
  const [show, setShow] = React.useState(true);
  const isLight = notification.variant === "light";
  const Icon = typeIcons[notification.type];

  useEffect(() => {
    if (notification.timeout !== false) {
      const timer = setTimeout(
        () => setShow(false),
        notification.timeout || 3000,
      );
      return () => clearTimeout(timer);
    }
  }, [notification.timeout]);

  return (
    <Transition
      show={show}
      as={Fragment}
      appear
      enter="transform ease-out duration-400 transition"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:ltr:translate-x-2 sm:rtl:-translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition-all ease-in duration-500"
      leaveFrom="opacity-100 max-h-[400px]"
      leaveTo="opacity-0 max-h-0"
      afterLeave={() => onRequestHide()}
    >
      <Alert
        className={classNames(
          "pointer-events-auto w-full max-w-sm overflow-hidden",
        )}
        color={isLight ? "light" : notification.type}
        icon={
          isLight ? (
            <FeatureIcon variant="outline" color={notification.type}>
              <Icon className="size-5" />
            </FeatureIcon>
          ) : undefined
        }
        title={notification.title}
        onClose={() => setShow(false)}
      >
        {notification.message}
        {notification.onOk && (
          <div className="mt-3 flex items-center gap-3">
            <div>
              <Button
                size="sm"
                color={isLight ? "link-color" : "primary"}
                variant={
                  isLight || notification.type === "primary"
                    ? "default"
                    : notification.type
                }
                className={isLight ? undefined : textColors[notification.type]}
                onClick={() => {
                  notification.onOk?.();
                }}
              >
                {notification.okText || "Ok"}
              </Button>
            </div>
          </div>
        )}
      </Alert>
    </Transition>
  );
}
